import React from 'react';
import styled from 'styled-components';
import { device } from '../../styles/devices';
import { color, gutter } from '../../styles/mixins';
import { m20, p12 } from '../../styles/typography';
import { Button } from '../button/button';
import { FormatAmount } from '../format-amount/format-amount';
import { Skeleton } from '../skeleton/skeleton';

const Host = styled.div`
  display: flex;
  align-items: center;
  padding: ${gutter(2)};
  border: 1px solid ${color('gray20')};
`;
const Left = styled.div``;

const Right = styled.div``;

const Image = styled.figure`
  margin-right: ${gutter(3)};
  width: 100px;
  aspect-ratio: 100 / 130;
  overflow: hidden;
  > * {
    width: 100%;
    height: 100%;
  }
`;

const Status = styled.div`
  margin-bottom: ${gutter(1)};

  @media ${device.desktop} {
    display: none;
  }
`;

const BrandName = styled.h2`
  ${m20};
  margin-bottom: ${gutter(1)};
`;

const Title = styled.p`
  margin-bottom: ${gutter(1)};
`;

const FairMarketValue = styled.div`
  display: flex;
  ${p12};
  color: ${color('gray80')};
  margin-bottom: ${gutter(0.5)};
`;

const Bid = styled.div`
  display: flex;
  ${p12};
`;

const Span = styled.span`
  display: inline-flex;
  min-width: 78px;
  margin-right: ${gutter(1.2)};
`;

const Desktop = styled.div`
  display: none;
  @media ${device.desktop} {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-left: auto;
    align-self: normal;
    align-items: flex-end;
  }
`;

const DesktopStatus = styled.div`
  display: none;
  @media ${device.desktop} {
    margin-bottom: ${gutter(1)};
    display: block;
  }
`;

interface LotCardProps {
  image: React.ReactNode;
  brandName: string;
  title: string;
  highestBid?: number | null;
  yourBid?: number;
  status?: React.ReactNode;
  timer?: React.ReactNode;
  isLoading?: boolean;
  summary?: string | null;
  buyersPremiumPercentage?: number;
  overheadPremiumPercentage?: number;
  isLotClosed?: boolean;
}

export const LotCard = ({
  image,
  brandName,
  yourBid,
  highestBid,
  status,
  timer,
  isLoading = false,
  summary = '',
  isLotClosed = false,
  buyersPremiumPercentage,
  overheadPremiumPercentage,
}: LotCardProps) => {
  return (
    <Host>
      <Left>
        <Image>
          {isLoading ? <Skeleton width={100} height={130} visible /> : image}
        </Image>
      </Left>
      <Right>
        {!isLoading && <Status>{status}</Status>}
        <BrandName>
          {isLoading ? <Skeleton height={26} width={160} visible /> : brandName}
        </BrandName>
        <Title>
          {isLoading ? <Skeleton height={22} width={100} visible /> : summary}
        </Title>
        {yourBid && (
          <FairMarketValue>
            {isLoading ? (
              <Skeleton height={21} width={160} visible />
            ) : (
              <>
                <Span>Your bid:</Span>
                <FormatAmount
                  value={yourBid}
                  buyersPremiumPercentage={
                    isLotClosed ? buyersPremiumPercentage : undefined
                  }
                  overheadPremiumPercentage={
                    isLotClosed ? overheadPremiumPercentage : undefined
                  }
                />
              </>
            )}
          </FairMarketValue>
        )}
        {!yourBid && (
          <Bid>
            {isLoading ? (
              <Skeleton height={21} width={160} visible />
            ) : timer ? (
              <>{timer}</>
            ) : (
              <>
                <Span>Highest bid:</Span>
                {highestBid ? (
                  <FormatAmount
                    value={highestBid}
                    buyersPremiumPercentage={
                      isLotClosed ? buyersPremiumPercentage : undefined
                    }
                    overheadPremiumPercentage={
                      isLotClosed ? overheadPremiumPercentage : undefined
                    }
                  />
                ) : (
                  'No bids yet'
                )}
              </>
            )}
          </Bid>
        )}
      </Right>
      <Desktop>
        <DesktopStatus>{!isLoading && status}</DesktopStatus>
        <Button>View Lot</Button>
      </Desktop>
    </Host>
  );
};
